import React, { useState, useEffect } from 'react';
import { exportCSV } from '../db';
import { getToken } from '../storage';
import { getApiBase } from '../apiBase';
import { Badge, Btn, Card, NoData, Search, Select, toast } from '../ui.jsx';

function Attendance({ db, save, activeSessionId }) {
  const [tab, setTab] = useState(0);
  const [cls, setCls] = useState('');
  const [dt, setDt] = useState(new Date().toISOString().split('T')[0]);
  const [attMap, setAttMap] = useState({});
  const [repCls, setRepCls] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'ok' | 'err'
  const classes = db.classes.map(c => c.name);
  const students = db.students.filter(s => s.cls === cls);
  const saved = (db.att[dt] || {});

  useEffect(() => {
    const currentStudents = db.students.filter(s => s.cls === cls);
    const todaySaved = (db.att[dt] || {});
    console.log('[Attendance] useEffect - cls:', cls, 'dt:', dt, 'db.att keys:', Object.keys(db.att || {}), 'todaySaved:', todaySaved);
    const m = {};
    currentStudents.forEach(s => { m[s.id] = todaySaved[s.id] || 'P'; });
    setAttMap(m);
  }, [cls, dt, db.att, db.students]);

  const saveAtt = async () => {
    if (!cls || !dt) { toast('Select class and date', 'err'); return; }

    const sessionId = activeSessionId
      || localStorage.getItem('lkps_active_session')
      || db?.settings?.sessionId;

    if (!sessionId) { toast('No active session — please refresh', 'err'); return; }

    setSaving(true);
    setSaveStatus(null);

    try {
      const records = {};
      students.forEach(s => { records[s.id] = attMap[s.id] || 'P'; });

      const token = getToken();
      const apiBase = getApiBase();
      const url = `${apiBase}/sessions/${sessionId}/attendance`;

      console.log('[Attendance] Saving to:', url);
      console.log('[Attendance] sessionId:', sessionId);
      console.log('[Attendance] date:', dt, 'cls:', cls);
      console.log('[Attendance] records:', records);

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: 'Bearer ' + token } : {}),
        },
        body: JSON.stringify({ date: dt, cls, records }),
      });

      const data = await res.json().catch(() => ({}));
      console.log('[Attendance] Response:', res.status, data);

      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      // Verify: immediately re-fetch to confirm the save persisted
      const verifyRes = await fetch(`${apiBase}/sessions/${sessionId}/attendance?date=${dt}&cls=${encodeURIComponent(cls)}`, {
        headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      });
      const verifyData = await verifyRes.json().catch(() => ({}));
      console.log('[Attendance] Verify fetch after save:', JSON.stringify(verifyData));

      // Update local db state so History tab reflects the save immediately
      const updatedAtt = { ...db.att, [dt]: { ...(db.att[dt] || {}), ...records } };
      save({ ...db, att: updatedAtt });

      setSaveStatus('ok');
      toast('Attendance saved ✓');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      console.error('[Attendance] Save error:', e);
      setSaveStatus('err');
      toast(`Save failed: ${e.message || 'Unknown error'}`, 'err');
      setTimeout(() => setSaveStatus(null), 5000);
    } finally {
      setSaving(false);
    }
  };
  const allP = () => { const m = {}; students.forEach(s => { m[s.id] = 'P'; }); setAttMap(m); };

  const present = Object.values(attMap).filter(v => v === 'P').length;
  const absent = Object.values(attMap).filter(v => v === 'A').length;
  const late = Object.values(attMap).filter(v => v === 'L').length;
  const total = students.length;
  const pct = total > 0 ? ((present + late) / total * 100).toFixed(1) : '0.0';

  const inCls = 'w-full bg-surface-container-lowest border-0 rounded-lg py-3 px-4 text-on-surface font-medium focus:ring-2 focus:ring-primary/10 focus:outline-none text-sm';

  return (
    <div>
      {/* Page Header */}
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-on-surface-variant text-xs font-bold uppercase tracking-widest mb-2">Student Records</p>
          <h1 className="text-4xl font-extrabold text-primary font-headline tracking-tight">Daily Roll Call</h1>
        </div>
        <div className="flex bg-surface-container-low p-1 rounded-xl">
          <button onClick={() => setTab(0)}
            className={`px-6 py-2 rounded-lg font-bold text-sm transition-all border-0 cursor-pointer ${tab === 0 ? 'bg-surface-container-lowest text-primary shadow-sm' : 'text-on-surface-variant hover:text-primary bg-transparent'}`}>
            Daily Entry
          </button>
          <button onClick={() => setTab(1)}
            className={`px-6 py-2 rounded-lg font-bold text-sm transition-all border-0 cursor-pointer ${tab === 1 ? 'bg-surface-container-lowest text-primary shadow-sm' : 'text-on-surface-variant hover:text-primary bg-transparent'}`}>
            History
          </button>
        </div>
      </section>

      {tab === 0 ? (
        <>
          {/* Filters + Stats */}
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
            {/* Filter Panel */}
            <div className="lg:col-span-4">
              <div className="bg-surface-container-low p-8 rounded-xl space-y-6">
                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Select Academic Grade</label>
                  <div className="relative">
                    <select value={cls} onChange={e => setCls(e.target.value)} className={inCls + ' appearance-none pr-10'}>
                      <option value="">— Select Class —</option>
                      {classes.map(c => <option key={c}>{c}</option>)}
                    </select>
                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-sm">expand_more</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Select Date</label>
                  <div className="relative">
                    <input type="date" value={dt} onChange={e => setDt(e.target.value)} className={inCls + ' pr-10'} />
                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-sm">calendar_today</span>
                  </div>
                </div>
                {cls && (
                  <div className="pt-4 border-t border-outline-variant/20 flex items-start gap-3 text-on-surface-variant">
                    <span className="material-symbols-outlined text-primary text-sm mt-0.5">info</span>
                    <p className="text-xs leading-relaxed italic">
                      {students.length} students in {cls}. Mark attendance for {new Date(dt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Bento */}
            <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-surface-container-lowest p-6 rounded-xl flex flex-col justify-between group hover:bg-primary transition-colors duration-300 cursor-default">
                <span className="material-symbols-outlined text-primary bg-primary-fixed p-3 rounded-full w-fit group-hover:bg-white/20 group-hover:text-white">groups</span>
                <div className="mt-8">
                  <p className="text-on-surface-variant text-xs font-bold uppercase tracking-widest group-hover:text-white/70">Total Students</p>
                  <p className="text-3xl font-extrabold font-headline mt-1 text-primary group-hover:text-white">{total}</p>
                </div>
              </div>
              <div className="bg-surface-container-lowest p-6 rounded-xl flex flex-col justify-between group hover:bg-secondary-container transition-colors duration-300 cursor-default">
                <span className="material-symbols-outlined text-on-secondary-container bg-secondary-fixed p-3 rounded-full w-fit group-hover:bg-white/20">check_circle</span>
                <div className="mt-8">
                  <p className="text-on-surface-variant text-xs font-bold uppercase tracking-widest group-hover:text-on-secondary-container/70">Present</p>
                  <p className="text-3xl font-extrabold font-headline mt-1 text-secondary group-hover:text-on-secondary-container">{String(present).padStart(2, '0')}</p>
                </div>
              </div>
              <div className="bg-surface-container-lowest p-6 rounded-xl flex flex-col justify-between group hover:bg-error-container transition-colors duration-300 cursor-default">
                <span className="material-symbols-outlined text-on-error-container bg-error-container p-3 rounded-full w-fit group-hover:bg-white/20">cancel</span>
                <div className="mt-8">
                  <p className="text-on-surface-variant text-xs font-bold uppercase tracking-widest group-hover:text-on-error-container/70">Absent</p>
                  <p className="text-3xl font-extrabold font-headline mt-1 text-error group-hover:text-on-error-container">{String(absent).padStart(2, '0')}</p>
                </div>
              </div>
              <div className="bg-surface-container-lowest p-6 rounded-xl flex flex-col justify-between group hover:bg-primary-container transition-colors duration-300 cursor-default">
                <span className="material-symbols-outlined text-on-primary-fixed bg-primary-fixed p-3 rounded-full w-fit group-hover:bg-white/20 group-hover:text-white">analytics</span>
                <div className="mt-8">
                  <p className="text-on-surface-variant text-xs font-bold uppercase tracking-widest group-hover:text-white/70">Attendance %</p>
                  <p className="text-3xl font-extrabold font-headline mt-1 text-primary group-hover:text-white">{pct}%</p>
                </div>
              </div>
            </div>
          </section>

          {/* Attendance Table */}
          <section className="bg-surface-container-lowest rounded-2xl overflow-hidden">
            <div className="p-8 flex items-center justify-between border-b border-surface-container-high">
              <div className="flex items-center gap-4">
                <h3 className="text-2xl font-extrabold font-headline text-primary">Student Registry</h3>
                {cls && <span className="px-3 py-1 bg-surface-container text-on-surface-variant text-[10px] font-bold tracking-widest uppercase rounded-full">{cls}</span>}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={allP}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-on-surface-variant hover:bg-surface-container transition-colors rounded-lg border-0 bg-transparent cursor-pointer">
                  <span className="material-symbols-outlined text-lg">done_all</span> All Present
                </button>
                <button onClick={saveAtt} disabled={saving}
                  className={`flex items-center gap-2 px-6 py-2.5 font-bold rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all border-0 cursor-pointer text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 ${
                    saveStatus === 'ok' ? 'bg-green-600 text-white' :
                    saveStatus === 'err' ? 'bg-red-600 text-white' :
                    'bg-primary text-on-primary'
                  }`}>
                  {saving
                    ? <><span className="material-symbols-outlined text-lg animate-spin">progress_activity</span> Saving…</>
                    : saveStatus === 'ok'
                    ? <><span className="material-symbols-outlined text-lg">check_circle</span> Saved!</>
                    : saveStatus === 'err'
                    ? <><span className="material-symbols-outlined text-lg">error</span> Failed — Retry</>
                    : <><span className="material-symbols-outlined text-lg">check_circle</span> Submit Attendance</>
                  }
                </button>
              </div>
            </div>

            {!cls || !dt ? (
              <div className="p-16 text-center">
                <span className="material-symbols-outlined text-5xl text-outline mb-4 block">fact_check</span>
                <p className="text-lg font-bold text-primary font-headline mb-1">Select a class and date</p>
                <p className="text-sm text-on-surface-variant">Use the filters on the left to get started.</p>
              </div>
            ) : !students.length ? (
              <div className="p-16 text-center">
                <span className="material-symbols-outlined text-5xl text-outline mb-4 block">group_off</span>
                <p className="text-lg font-bold text-primary font-headline mb-1">No students in this class</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-surface-container-low/50">
                      <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Roll No.</th>
                      <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Student Identity</th>
                      <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Gender / Class</th>
                      <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Status</th>
                      <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-container-high">
                    {students.map((s, idx) => {
                      const val = attMap[s.id] || 'P';
                      const photo = db.photos?.[s.id];
                      return (
                        <tr key={s.id} className="hover:bg-surface-container-low transition-colors duration-200">
                          <td className="px-8 py-6 font-headline font-bold text-on-surface">{s.roll || String(idx + 1).padStart(3, '0')}</td>
                          <td className="px-8 py-6">
                            <div className="flex items-center gap-4">
                              <div className="h-10 w-10 rounded-full bg-primary/10 overflow-hidden flex-shrink-0 flex items-center justify-center font-bold text-primary text-sm">
                                {photo
                                  ? <img src={photo} alt={s.fn} className="w-full h-full object-cover" />
                                  : (s.fn[0] || '') + (s.ln[0] || '')}
                              </div>
                              <div>
                                <p className="font-headline font-bold text-primary">{s.fn} {s.ln}</p>
                                <p className="text-xs text-on-surface-variant">{s.admno ? `Adm: ${s.admno}` : s.cls}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-8 py-6 text-on-surface-variant text-sm font-medium">{s.gn || 'Male'} / {s.cls}</td>
                          <td className="px-8 py-6">
                            <div className="flex gap-2">
                              {[['P', 'Present', 'bg-secondary-container text-on-secondary-container border-secondary'],
                              ['A', 'Absent', 'bg-error-container text-on-error-container border-error'],
                              ['L', 'Leave', 'bg-cyan-100 text-cyan-800 border-cyan-300']
                              ].map(([v, label, activeStyle]) => (
                                <label key={v} className="relative flex items-center justify-center cursor-pointer">
                                  <input type="radio" name={`att-${s.id}`} value={v} checked={val === v}
                                    onChange={() => setAttMap(m => ({ ...m, [s.id]: v }))} className="peer hidden" />
                                  <span className={`px-4 py-1.5 rounded-full border text-xs font-bold transition-all select-none
                                    ${val === v ? activeStyle : 'border-outline-variant text-on-surface-variant hover:border-outline'}`}>
                                    {label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </td>
                          <td className="px-8 py-6">
                            <input
                              className={`bg-transparent border-b border-transparent focus:border-primary/30 focus:ring-0 text-sm py-1 w-full transition-all outline-none ${val === 'A' ? 'text-error font-medium' : 'text-on-surface'}`}
                              placeholder="Add note..."
                              defaultValue=""
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {students.length > 0 && (
              <div className="p-8 border-t border-surface-container-high bg-surface-container-low/30">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-on-surface-variant">
                    Showing {students.length} student{students.length !== 1 ? 's' : ''} in {cls || '—'}
                  </p>
                  <div className="flex items-center gap-4 text-sm font-bold">
                    <span className="text-secondary">P: {present}</span>
                    <span className="text-error">A: {absent}</span>
                    <span className="text-on-tertiary-fixed-variant">L: {late}</span>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Footer Insight */}
          {cls && (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-8 py-8 mt-8 border-t border-outline-variant/30">
              <div className="space-y-4">
                <h4 className="font-headline font-bold text-primary flex items-center gap-2">
                  <span className="material-symbols-outlined">trending_up</span> Attendance Insight
                </h4>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  Today's attendance for <span className="text-primary font-bold">{cls}</span> is{' '}
                  <span className={`font-bold ${parseFloat(pct) >= 90 ? 'text-secondary' : parseFloat(pct) >= 75 ? 'text-on-tertiary-fixed-variant' : 'text-error'}`}>{pct}%</span>.{' '}
                  {absent > 0 ? `${absent} student${absent > 1 ? 's are' : ' is'} absent today.` : 'Full attendance today.'}
                </p>
              </div>
              <div className="flex items-center justify-end gap-6">
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Session</p>
                  <p className="font-headline font-bold text-primary">{db.settings.year || '2025-26'}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center text-on-primary font-bold text-lg">
                  {pct}
                </div>
              </div>
            </section>
          )}
        </>
      ) : (
        /* History / Report Tab */
        <div>
          <div className="flex items-center gap-4 mb-8">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Class</label>
              <select value={repCls} onChange={e => setRepCls(e.target.value)}
                className="bg-surface-container-lowest border-0 rounded-xl px-4 py-2.5 font-semibold text-primary shadow-sm ring-1 ring-outline-variant/20 focus:ring-primary focus:outline-none text-sm cursor-pointer">
                <option value="">Select Class</option>
                {classes.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {!repCls ? (
            <div className="bg-surface-container-lowest rounded-2xl p-16 text-center">
              <span className="material-symbols-outlined text-5xl text-outline mb-4 block">history</span>
              <p className="text-lg font-bold text-primary font-headline mb-1">Select a class to view history</p>
            </div>
          ) : (
            <div className="bg-surface-container-lowest rounded-2xl overflow-hidden">
              <div className="p-8 border-b border-surface-container-high flex items-center gap-4">
                <h3 className="text-2xl font-extrabold font-headline text-primary">Attendance Report</h3>
                <span className="px-3 py-1 bg-surface-container text-on-surface-variant text-[10px] font-bold tracking-widest uppercase rounded-full">{repCls}</span>
              </div>
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-surface-container-low/50">
                    <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Student</th>
                    <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Present</th>
                    <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Absent</th>
                    <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Late</th>
                    <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Attendance %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-container-high">
                  {db.students.filter(s => s.cls === repCls).map(s => {
                    let p = 0, a = 0, l = 0;
                    Object.values(db.att).forEach(d => {
                      const v = d[s.id];
                      if (v === 'P') p++;
                      else if (v === 'L') l++;
                      else if (v === 'A') a++;
                    });
                    const tot = p + a + l;
                    const pct = tot > 0 ? Math.round((p + l) / tot * 100) : 0;
                    const barColor = pct >= 90 ? 'bg-secondary' : pct >= 75 ? 'bg-on-tertiary-container' : 'bg-error';
                    const textColor = pct >= 90 ? 'text-secondary' : pct >= 75 ? 'text-on-tertiary-fixed-variant' : 'text-error';
                    return (
                      <tr key={s.id} className="hover:bg-surface-container-low transition-colors">
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center font-bold text-primary text-xs flex-shrink-0">
                              {(s.fn[0] || '') + (s.ln[0] || '')}
                            </div>
                            <span className="font-semibold text-primary">{s.fn} {s.ln}</span>
                          </div>
                        </td>
                        <td className="px-8 py-5 text-secondary font-bold">{p}</td>
                        <td className="px-8 py-5 text-error font-bold">{a}</td>
                        <td className="px-8 py-5 text-on-tertiary-fixed-variant font-bold">{l}</td>
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-1.5 bg-surface-container-low rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${barColor}`} style={{ width: pct + '%' }}></div>
                            </div>
                            <span className={`text-xs font-bold ${textColor}`}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default Attendance;
